/**
* Taxi Conditionality Graph
*
* The full design and architectural documentation for this graph is maintained in:
*     /docs/design/taxi-conditionality.md*
*
* Please refer to that document for:
* - Graph structure and vertex types
* - Path and condition string generation
* - Branching model assumptions
* - Rootable vertices
* - Design constraints and limitations
*
* This file contains the implementation of the ConditionalityGraph only,
* while the documentation file is the single source of truth for design.
*/

/* eslint-disable no-param-reassign */
import _ from 'lodash';

const OPERATIONS = [
  'EQ',
  'NE',
  'GT',
  'GE',
  'LT',
  'LE',
  'NOT',
  'AND',
  'OR',
];

function tokenizeString(rawCondition) {
  const splitter = new RegExp(`(\\s*${OPERATIONS.join('|')}|\\(|\\)\\s*)`, 'g');
  const preTokens = rawCondition.split(splitter);
  const actualTokens = [];
  let combinator = '';
  preTokens.forEach((token) => {
    const trimmed = token.trim();
    if (combinator.length === 0) {
      if (!trimmed.startsWith('"')) {
        actualTokens.push(trimmed);
      } else if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        actualTokens.push(trimmed.slice(1, -1));
      } else {
        combinator += token;
      }
    } else {
      combinator += token;
      if (trimmed.endsWith('"')) {
        actualTokens.push(combinator.trim().slice(1, -1).trim());
        combinator = '';
      }
    }
  });
  if (combinator.length) {
    actualTokens.push(combinator.trim());
  }
  return actualTokens.filter((token) => token.length);
}

function makeInfixToken(token) {
  const obj = {};
  const opIdx = OPERATIONS.indexOf(token);
  if (opIdx > -1) {
    obj.op = token;
  } else if (token === '(') {
    obj.infixOp = 1;
  } else if (token === ')') {
    obj.infixOp = -1;
  } else {
    obj.data = token;
  }
  return obj;
}

function parseCondition(condition) {
  return tokenizeString(condition).map(makeInfixToken);
}

function canBeCondition(vertex) {
  return (
    vertex.left !== undefined &&
    vertex.right !== undefined &&
    vertex.op !== undefined
  );
}

function tryReduceUselessParens(stack) {
  if (stack.length < 3) {
    return stack;
  }
  const taken = [];
  taken.push(stack.pop());
  taken.push(stack.pop());
  taken.push(stack.pop());
  if (taken[0].infixOp === -1 && taken[2].infixOp === 1) {
    stack.push(taken[1]);
  } else {
    taken.reverse().forEach((e) => stack.push(e));
  }
  return stack;
}

function tryReduceVertex(stack, graph, stateEntryToFieldIdMap) {
  if (stack.length < 3) return stack;
  const taken = [];
  let abort = false;
  let expect = 'D';
  while (stack.length && !abort && taken.length < 5) {
    const next = stack.pop();
    if (expect === 'D') {
      if (next.data !== undefined) {
        expect = 'NO';
        taken.push(next);
      } else {
        stack.push(next);
        abort = true;
      }
    } else if (expect === 'NO') {
      if (next.op !== undefined) {
        if (next.op === 'NOT') {
          expect = 'O';
        } else {
          expect = 'D';
        }
        taken.push(next);
      } else {
        stack.push(next);
        abort = true;
      }
    } else if (expect === 'O') {
      if (next.op !== undefined) {
        if (next.op === 'NOT') {
          abort = true;
        } else {
          expect = 'D';
          taken.push(next);
        }
      } else {
        stack.push(next);
        abort = true;
      }
    }
  }

  const vertex = taken.reduce((memo, token, i) => {
    if (token.data !== undefined) {
      if (i !== 0) {
        memo.left = stateEntryToFieldIdMap[token.data] ? stateEntryToFieldIdMap[token.data] : token.data;
      } else {
        memo.right = stateEntryToFieldIdMap[token.data] ? stateEntryToFieldIdMap[token.data] : token.data;
      }
    } else if (token.op !== undefined) {
      if (token.op === 'NOT') {
        if (i === 1) {
          memo.negateRight = true;
        } else {
          memo.negateLeft = true;
        }
      } else {
        memo.op = token.op;
      }
    }
    return memo;
  }, {});

  if (!canBeCondition(vertex)) {
    taken.reverse().forEach((x) => stack.push(x));
  } else {
    stack.push({ vertex: graph.findOrCreateVertex(vertex) });
  }
  return tryReduceUselessParens(stack);
}

function takeGreedy(stack) {
  let depth = 0;
  let waitingForFirstParen = true;
  const taken = [];
  do {
    const next = stack.shift();
    taken.push(next);
    if (next.infixOp !== undefined) {
      waitingForFirstParen = false;
      depth += next.infixOp;
    }
  } while (stack.length && (waitingForFirstParen || depth));
  if (taken[0].infixOp !== undefined && taken[taken.length - 1].infixOp !== undefined) {
    return taken.slice(1, taken.length - 1);
  }
  return taken;
}

function makeTree(root, stack) {
  while (stack.length) {
    const taken = takeGreedy(stack);
    if (!taken.length) return;
    if (taken[0].vertex) {
      const node = { vertex: taken.shift().vertex, up: root, down: [] };
      root.down.push(node);
    }
    if (taken[0]) {
      if (taken[0].op === 'AND') {
        taken.shift();
        makeTree(root.down[root.down.length - 1], taken);
      } else if (taken[0].op === 'OR') {
        taken.shift();
        makeTree(root, taken);
      }
    }
  }
}

function insertIntoGraph(graph, end, tree, backstack = []) {
  if (tree.down.length) {
    tree.down.forEach((node) => insertIntoGraph(graph, end, node, [...backstack, tree.vertex]));
  } else {
    graph.addPath([...backstack, tree.vertex, end], end);
  }
}

function condVertCmp(a, b) {
  return a.left === b.left && a.right === b.right && a.op === b.op && a.not.left === b.not.left && a.not.right === b.not.right;
}

function fieldVertCmp(a, b) {
  return a.left === b.left;
}

function defaultCmp(a, b) {
  if (a === null && b === null) {
    return true;
  }
  if (a === undefined && b === undefined) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  if (a.type !== b.type) {
    return false;
  }
  switch (a.type) {
    case FIELD_VERTEX:
      return fieldVertCmp(a, b);
    case CONDITION_VERTEX:
      return condVertCmp(a, b);
    default:
      return true;
  }
}

const MAIN_ROOT_VERTEX = 0;
const FIELD_VERTEX = 1;
const CONDITION_VERTEX = 2;

const ALL_PATH_KEYS = '__ANY';

export default class ConditionalityGraph {
  constructor({ graph = [], orphanedUserBranches = [], roots = [], userBranches = {}, userVertexToBranchMap = {} }, comparator = null) {
    this._comparator = comparator || defaultCmp;
    if (graph.length) {
      this._graph = graph;
    } else {
      this._graph = [this._makeVertex({})];
    }
    if (roots.length) {
      this._roots = roots;
      this._staleRoots = false;
    } else {
      this._staleRoots = true;
    }
    this._orphanedUserBranches = orphanedUserBranches;
    this._userBranches = userBranches;
    this._userVertexToBranchMap = userVertexToBranchMap;
  }

  _findVertexIndex(details) {
    if (typeof details === 'number' || details === null) {
      return details;
    }
    if (details.left === undefined) {
      throw new Error(`Expected vertex object, received ${details}`);
    }
    const criteria = this._makeVertex(details);
    return this._graph.findIndex((c) => this._comparator(c, criteria));
  }

  findOrCreateVertex({ left, op = null, right = null, negateLeft = false, negateRight = false, action = '' }, edges = {}) {
    const idx = this._findVertexIndex({ left, op, right, not: { left: negateLeft, right: negateRight }, action, edges });
    if (idx !== -1) {
      return idx;
    }
    this._graph.push(this._makeVertex({ left, op, right, negateLeft, negateRight, action, edges }));
    return this._graph.length - 1;
  }

  _findEdgeIndex(edges, up, down) {
    if (!edges) return -1;
    return edges.findIndex((i) => i.up === up && i.down === down);
  }

  _connectVertex(up, vertex, down, pathKey) {
    const vertexIndex = this._findVertexIndex(vertex);
    const upIndex = this._findVertexIndex(up);
    const downIndex = this._findVertexIndex(down);
    const vertexEdges = this._graph[vertexIndex].edges;
    if (vertexEdges[pathKey]) {
      if (vertexEdges[pathKey].findIndex((e) => e.up === upIndex && e.down === downIndex) === -1) {
        vertexEdges[pathKey].push({ up: upIndex, down: downIndex });
      }
    } else {
      vertexEdges[pathKey] = [{ up: upIndex, down: downIndex }];
    }
    this._staleRoots = true;
  }

  // Suggested refactor: give the vertex a link up to root if it's a field
  _makeVertex(vertexDetails) {
    const { left = null, op = null, right = null, negateLeft = false, negateRight = false, action = '', edges = {} } = vertexDetails;
    if (left === null) {
      return { edges, type: MAIN_ROOT_VERTEX };
    }
    if (op === null) {
      return { left, action, edges, type: FIELD_VERTEX };
    }
    return {
      left,
      right,
      op,
      edges,
      type: CONDITION_VERTEX,
      not: {
        left: negateLeft,
        right: negateRight,
      },
    };
  }

  _getAllEdgesForVertex(vertex) {
    return Object.values(vertex.edges).reduce((memo, edgeArray) => memo.concat(edgeArray), []);
  }

  _makeCondObject(vertexList, downVertex, rootVertex, pathMap, replacements) {
    vertexList.forEach((v) => {
      if (v === null) return;
      const vertex = this._graph[v];
      const left = vertex.left !== null && replacements[vertex.left] ? `state.${replacements[vertex.left]}` : `"${vertex.left}"`;
      const right = vertex.type === CONDITION_VERTEX && replacements[vertex.right] ? `state.${replacements[vertex.right]}` : `"${vertex.right}"`;
      const condString = vertex.type !== CONDITION_VERTEX ? '' : `(${left} ${vertex.op} ${right})`;
      pathMap[v] = pathMap[v] ? pathMap[v] : [condString];
      if (pathMap[v].findIndex((a) => a === downVertex) === -1) {
        pathMap[v].push(downVertex);
      }
      this._makeCondObject(vertex.edges[rootVertex].filter((x) => x.down === downVertex).map((x) => x.up), v, rootVertex, pathMap, replacements);
    });
    return pathMap;
  }

  _writeStringFromCondObj(current, condObj, skipBranchVertices, branchingVertices) {
    if (!condObj[current].length) return null;
    if (!condObj[current][0].length) {
      return condObj[current].slice(1).map((v) => this._writeStringFromCondObj(v, condObj, skipBranchVertices, branchingVertices)).filter((x) => x !== null).filter((x) => x.length).map((x) => `(${x})`).join(' OR ');
    }
    const right = condObj[current].slice(1).map((v) => this._writeStringFromCondObj(v, condObj, skipBranchVertices, branchingVertices)).filter((x) => x !== null).filter((x) => x.length).join(' OR ');
    if (skipBranchVertices && branchingVertices.includes(current)) {
      return right;
    }
    return `${condObj[current][0]}${right.length ? ' AND (' : ''}${right}${right.length ? ')' : ''}`;
  }

  _makeConditionStringForRoot(rootIndex, activeBranchAnswers, replacements) {
    const rootVertex = this._graph[rootIndex];
    const ups = this._graph[rootIndex].edges[rootIndex].map((x) => x.up);

    const branchingFields = Object.keys(this._userVertexToBranchMap);
    const branchingVertexIndices = activeBranchAnswers.map((answer) => this._userBranches[branchingFields[0]][answer]);
    const fieldBranchingVertices = this.getNearestBranchVerticesForFieldVertex(rootIndex, branchingFields[0]).map((answer) => this._userBranches[branchingFields[0]][answer]);
    const intersection = _.intersection(fieldBranchingVertices, branchingVertexIndices);
    const onAllBranches = (branchingVertexIndices.length && intersection.length === branchingVertexIndices.length);

    const condObj = this._makeCondObject(ups, rootIndex, rootIndex, { [rootIndex]: [] }, replacements);
    const conditionString = this._writeStringFromCondObj(0, condObj, onAllBranches, branchingVertexIndices);
    if (conditionString && conditionString.trim().length) {
      return `${rootVertex.action} ${conditionString}`;
    }
    return '';
  }

  _isRoot(vertexIndex) {
    if (vertexIndex === 0) return true;
    if (this._graph[vertexIndex].type === FIELD_VERTEX && Object.keys(this._graph[vertexIndex].edges).length) return true;
    return false;
  }

  /* Used to replace the contents of a fieldVertex with those of newVertex, for example to update the action of
   * a field vertex. Expected to become obsolete when we support multiple actions on a single field vertex. */
  replaceOrInsertVertex(vertex, newVertex, andDisassociate = false) {
    const vertexIndex = typeof vertex === 'number' ? vertex : this.findOrCreateVertex(vertex);
    this._graph[vertexIndex] = { ...this._graph[vertexIndex], ...newVertex };
    if (andDisassociate || !this._graph[vertexIndex].edges.length) {
      this.orphanFieldVertex(vertexIndex);
      this.addPath([0, vertexIndex], vertexIndex);
    }
    return vertexIndex;
  }

  dehydrate() {
    return {
      graph: this._graph,
      orphanedUserBranches: this._staleRoots ? this.getOrphanedUserBranches() : this._orphanedUserBranches,
      roots: this._staleRoots ? this.getRoots() : this._roots,
      userBranches: this._userBranches,
      userVertexToBranchMap: this._userVertexToBranchMap,
    };
  }

  getRoots() {
    if (!this._staleRoots) {
      return this._roots;
    }
    const roots = [];
    this._graph.forEach((vertex, idx) => {
      if (this._isRoot(idx)) {
        roots.push(idx);
      }
    });
    if (roots.length === 0) {
      roots.push(0);
    }
    this._roots = roots;
    this._staleRoots = false;
    return roots;
  }

  getOrphanedUserBranches() {
    if (this._orphanedUserBranches.length && !this._staleRoots) {
      return this._orphanedUserBranches;
    }
    this._orphanedUserBranches = _.flattenDeep(
      Object.keys(this._userBranches)
        .map((fieldId) => Object.keys(this._userVertexToBranchMap[fieldId] || {})
          .filter((vertex) => this.isVertexOrphaned(+vertex))))
      .map((v) => +v);
    return this._orphanedUserBranches;
  }

  _removeMiddleIfTripletInList(list, [left, middle, right]) {
    const index = list.lastIndexOf(right);
    if (index === -1 || index < 2) return list;
    if (list[index - 1] === middle && list[index - 2] === left) {
      const removed = [...list];
      removed.splice(index - 1, 1);
      return removed;
    }
    return list;
  }

  /* Does nothing to the noop root vertex */
  removeVertexFromPath(vertex, up, down, pathKey) {
    const vertexIndex = this._findVertexIndex(vertex);
    if (!vertexIndex) {
      return;
    }
    if (pathKey === ALL_PATH_KEYS) {
      Object.keys(this._graph[vertexIndex].edges).forEach((eachPathKey) => {
        this.removeVertexFromPath(vertex, up, down, eachPathKey);
      });
      return;
    }

    const upIndex = this._findVertexIndex(up);
    const downIndex = this._findVertexIndex(down);
    const edgeIndex = this._findEdgeIndex(this._graph[vertexIndex].edges[pathKey], upIndex, downIndex);
    if (edgeIndex === -1) {
      return;
    }
    const allPaths = this.getAllPathsFromVertex(downIndex, pathKey);
    this._graph[vertexIndex].edges[pathKey].splice(edgeIndex, 1);

    // The up half of the up vertex's edges that point down to vertex
    const upList = this._graph[upIndex].edges[pathKey].filter((x) => x.down === vertexIndex).map((x) => x.up);
    // The up half of the up vertex's edges that point down to vertex
    const downList = downIndex ? this._graph[downIndex].edges[pathKey].filter((x) => x.up === vertexIndex).map((x) => x.down) : [];
    const shortenedPaths = allPaths.map((path) => this._removeMiddleIfTripletInList(path, [upIndex, vertexIndex, downIndex])).reduce((memo, maybeShorter, index) => {
      if (maybeShorter.length < allPaths[index].length) {
        memo.push([index, maybeShorter]);
      }
      return memo;
    }, []);
    shortenedPaths.forEach(([index, path]) => {
      for (let i = 0; i < allPaths.length; i++) {
        if (i !== index && _.intersection(allPaths[i], path).length === path.length) {
          const upListIndex = upIndex === 0 ? 0 : upList.lastIndexOf(path[path.lastIndexOf(upIndex) - 1]);
          if (upListIndex > -1) {
            upList.splice(upListIndex, 1);
          }
        }
      }
    });
    upList.forEach((upEdge) => downList.forEach((downEdge) => {
      this._connectVertex(upIndex, downIndex, downEdge, pathKey);
      this._connectVertex(upEdge, upIndex, downIndex, pathKey);
    }));
    let shouldDisconnectUp = true;
    let shouldDisconnectDown = downIndex !== null;
    this._graph[vertexIndex].edges[pathKey].forEach((e) => {
      if (e.up === up) shouldDisconnectUp = false;
      if (e.down === down) shouldDisconnectDown = false;
    });
    if (shouldDisconnectUp) {
      this._graph[upIndex].edges[pathKey] = this._graph[upIndex].edges[pathKey].filter((x) => x.down !== vertexIndex);
    }
    if (shouldDisconnectDown) {
      this._graph[downIndex].edges[pathKey] = this._graph[downIndex].edges[pathKey].filter((x) => x.up !== vertexIndex);
    }
    this._staleRoots = true;
    if (this._graph[vertexIndex].edges[pathKey].length === 0) {
      delete this._graph[vertexIndex].edges[pathKey];
    }
  }

  addVertexToPath(vertex, up, down, pathKey) {
    const vertexIndex = this._findVertexIndex(vertex);
    const upIndex = this._findVertexIndex(up);
    const downIndex = this._findVertexIndex(down);

    const vertexInPath = this._findEdgeIndex(this._graph[vertexIndex].edges[pathKey], up, down) !== -1;
    if (!vertexInPath) {
      this._connectVertex(upIndex, vertexIndex, downIndex, pathKey);
      if (this._graph[upIndex].edges[pathKey]) {
        this._graph[upIndex].edges[pathKey] = this._graph[upIndex].edges[pathKey].map((e) => ({ up: e.up, down: e.down === downIndex ? vertexIndex : e.down })).filter((x, i, arr) => this._findEdgeIndex(arr, x.up, x.down) === i);
      }
      if (this._graph[downIndex].edges[pathKey]) {
        this._graph[downIndex].edges[pathKey] = this._graph[downIndex].edges[pathKey].map((e) => ({ up: e.up === upIndex ? vertexIndex : e.up, down: e.down })).filter((x, i, arr) => this._findEdgeIndex(arr, x.up, x.down) === i);
      }
    }
  }

  /* Takes a path (higher elements first) ending at a field, and adds each edge required for that path to be represented in the graph.
   * If insertPath does not start at the no-op root, we will connect this path up to all elements that both
   * connect down to the first (highest up) element of insertPath and which are already associated with the given pathKey. */
  addPath(insertPath, pathKey) {
    const path = [...insertPath];
    if (path[0] === 0) {
      this._connectVertex(null, path[0], path[1], pathKey);
    } else {
      this._graph[path[0]].edges[pathKey].forEach((edge) => this._connectVertex(edge.up, path[0], path[1], pathKey));
    }
    let i = 1;
    for (; i < path.length - 1; i++) {
      this._connectVertex(path[i - 1], path[i], path[i + 1], pathKey);
    }
    this._connectVertex(path[i - 1], path[i], null, pathKey);
  }

  setUserBranches(fieldId, possibleValues) {
    this._userBranches[fieldId] = possibleValues.reduce((memo, value) => {
      memo[value] = this.findOrCreateVertex({ left: fieldId, op: 'EQ', right: value });
      return memo;
    }, {});
    this._userVertexToBranchMap[fieldId] = Object.keys(this._userBranches[fieldId]).reduce((memo, key) => {
      memo[this._userBranches[fieldId][key]] = key;
      return memo;
    }, {});
  }

  unsetUserBranches(field) {
    if (this._userBranches[field]) {
      Object.keys(this._userBranches[field]).forEach((right) => this.orphanVertex({ left: field, op: 'EQ', right }));
      delete this._userBranches[field];
    }
  }

  getUserBranchingFields() {
    return Object.keys(this._userBranches);
  }

  getVerticiesForUserBranchingField(fieldId) {
    return this._userVertexToBranchMap[fieldId] || null;
  }

  /* This function assumes that any branching vertices are immediately above field vertices */
  getNearestBranchVerticesForFieldVertex(fieldVertex, branchingFieldId) {
    const fieldVertexIndex = typeof fieldVertex === 'number' ? fieldVertex : this.findOrCreateVertex(fieldVertex);
    const branchingVertexList = this._userBranches[branchingFieldId] ? Object.values(this._userBranches[branchingFieldId]) : [];
    return this._graph[fieldVertexIndex].edges[fieldVertexIndex].map((edge) => edge.up).filter((edgeUp) => branchingVertexList.includes(edgeUp)).map((vertex) => this._userVertexToBranchMap[branchingFieldId][vertex]);
  }

  getVertexForUserBranchingAnswer(branchingField, branchingAnswer) {
    return this._userBranches[branchingField][branchingAnswer];
  }

  removeAdjacentVertexFromPaths(terminal, vertex) {
    const tIndex = typeof terminal === 'number' ? terminal : this.findOrCreateVertex(terminal);
    const vIndex = typeof vertex === 'number' ? vertex : this.findOrCreateVertex(vertex);

    // Refactor: If we make a separate function for non-field vertices, we can remove the _getAllEdges -> filter, and do edges[tIndex] instead
    const removableEdges = this._getAllEdgesForVertex(this._graph[vIndex]).filter((edge) => edge.up === tIndex || edge.down === tIndex);
    removableEdges.forEach((edge) => this.removeVertexFromPath(vIndex, edge.up, edge.down, ALL_PATH_KEYS));
  }

  /* Used to insert a branch condition vertex immediately above fieldVertex.
     If the field is on no branches, the branch vertex is inserted in all paths up from fieldVertex,
     If it is already on branches, we assume all the branch vertices we're connected to connect up to the
     same fields, and insert this branch between fieldVertex and all the vertices above the first branch vertex. */
  insertUserBranchingVertexAsAdjacentVertex(fieldVertex, insertVertex, branchingFieldId) {
    const fieldVertexIndex = typeof fieldVertex === 'number' ? fieldVertex : this.findOrCreateVertex(fieldVertex);
    const insertVertexIndex = typeof insertVertex === 'number' ? insertVertex : this.findOrCreateVertex(insertVertex);
    const branchingVerticies = this._userBranches[branchingFieldId] ? Object.values(this._userBranches[branchingFieldId]) : [];
    const priorBranches = this._graph[fieldVertexIndex].edges[fieldVertexIndex].filter((edge) => branchingVerticies.includes(edge.up)).map((edge) => edge.up);
    if (!priorBranches.length) {
      [...this._graph[fieldVertexIndex].edges[fieldVertexIndex]].forEach((edge) => {
        this.addVertexToPath(insertVertexIndex, edge.up, fieldVertexIndex, fieldVertexIndex);
      });
    } else {
      this._graph[priorBranches[0]].edges[fieldVertexIndex].forEach((edge) => this.addPath([edge.up, insertVertexIndex, fieldVertexIndex], fieldVertexIndex));
    }
  }

  /* Removes all edges from a vertex, and removes it from the edges of all vertices it is adjacent to.
     If this process turns a conditional vertex into a root (all down edges are null), orphan that vertex as well. */
  orphanVertex(vertex) {
    let vIndex = vertex;
    if (typeof vertex !== 'number') {
      vIndex = this._findVertexIndex(vertex);
    }
    if (vIndex === -1) return;
    if (vIndex === 0) {
      console.warn('You attempted to orphan the root vertex, this is probably not what you meant to do, so I didn\'t'); // eslint-disable-line
      return;
    }
    let processRoot = false;
    const disconnect = this._getAllEdgesForVertex(this._graph[vIndex]);
    disconnect.forEach((edge) => {
      this.removeVertexFromPath(vIndex, edge.up, edge.down, ALL_PATH_KEYS);
      if (edge.up && this._isRoot(edge.up)) {
        this.orphanVertex(edge.up);
      } else if (edge.up === null) {
        processRoot = true;
      }
    });
    if (processRoot) {
      Object.keys(this._graph[vIndex].edges).forEach((pathKey) => {
        this._graph[vIndex].edges[pathKey].forEach((edge, index) => {
          if (this._getAllEdgesForVertex(this._graph[edge.down]).length === 0) {
            this._graph[vIndex].edges[pathKey][index] = null;
          }
        });
        this._graph[vIndex].edges[pathKey] = disconnect.filter((x) => x !== null);
        if (this._graph[vIndex].edges[pathKey].length === 0) {
          delete this._graph[vIndex].edges[pathKey];
        }
      });
    }
  }

  /* Remove all edges in the graph associated with the given field. */
  orphanFieldVertex(vertex) {
    const vertexIndex = this._findVertexIndex(vertex);
    if (vertexIndex <= 0) {
      return;
    }
    this._orphanFieldVertex(vertexIndex, vertexIndex);
  }

  /* Follow pathKey's edges up from currentVertex, deleting all of pathKey's edges from all traveled vertices */
  _orphanFieldVertex(currentVertex, pathKey) {
    const currentVertexIndex = this._findVertexIndex(currentVertex);
    this._graph[currentVertexIndex].edges[pathKey].forEach((edge) => {
      if (edge.up !== null && this._graph[edge.up].edges[pathKey]) {
        this._orphanFieldVertex(edge.up, pathKey);
      }
    });
    delete this._graph[currentVertexIndex].edges[pathKey];
  }

  _getAllVerticiesInPathsToRoot(from) {
    const vertices = this._getAllEdgesForVertex(this._graph[from]).reduce((memo, nextEdge) => {
      if (nextEdge.up) {
        memo.push(this._getAllVerticiesInPathsToRoot(nextEdge.up));
      }
      return memo;
    }, []);
    return _.flattenDeep([from, ...vertices]);
  }

  getAllVerticiesInPathsToRoot(vertex) {
    const vertexIndex = this._findVertexIndex(vertex);
    if (vertexIndex < 1) {
      return [];
    }
    return this._getAllVerticiesInPathsToRoot(vertexIndex);
  }

  _getAllPathsFromVertex(vertexIndex, parent, pathList, pathKey) {
    const vertex = this._graph[vertexIndex];
    const edgeList = vertex.edges[pathKey].filter((edge) => edge.down === parent);
    const initialPaths = edgeList.map((edge) => [...pathList, edge.up]);
    const finalPaths = edgeList.map((edge, index) => edge.up ? this._getAllPathsFromVertex(edge.up, vertexIndex, initialPaths[index], pathKey) : initialPaths[index]);
    return finalPaths;
  }

  /* Returns an array of all paths up from vertex to the noop root.
     Works for both field and condition vertices. */
  getAllPathsFromVertex(vertex, pathKey) {
    const vertexIndex = this._findVertexIndex(vertex);
    if (vertexIndex < 1) {
      return [];
    }
    return _.flattenDeep(this._getAllPathsFromVertex(vertexIndex, null, [vertexIndex], pathKey)).reverse().reduce((memo, index) => {
      if (index) {
        memo[memo.length - 1].push(index);
      } else {
        memo.push([index]);
      }
      return memo;
    }, []);
  }

  isVertexOrphaned(vertex) {
    const vertexIndex = this._findVertexIndex(vertex);
    if (vertexIndex < 0) {
      return true;
    }
    return this._getAllEdgesForVertex(this._graph[vertexIndex]).length === 0;
  }

  isVertexGroupAlwaysReachable(vertexGroup, activeUserBranchingVerticies) {
    const group = vertexGroup.map((v) => this._findVertexIndex(v));

    // Since we assure branching vertices are immediately above fields, if any vertex doesn't go immediately to a branching vertex then it is on all branches.
    const allGroupEdges = group.reduce((memo, member) => memo.concat(this._getAllEdgesForVertex(this._graph[member])), []);
    if (allGroupEdges.reduce((last, edge) => !activeUserBranchingVerticies.includes(edge.up) || last, false)) {
      return true;
    }

    const allConnections = group.map((v) => this.getAllVerticiesInPathsToRoot(v)).reduce((memo, next) => [...memo, ...next], []);
    const uniqueConnections = allConnections.filter((x, idx, arr) => arr.findIndex((a) => a === x) === idx);
    if (uniqueConnections.length === 1) {
      return true;
    }
    const activeBranches = Object.keys(this._userBranches);
    if (!activeBranches.length) {
      return true;
    }
    if (vertexGroup.some((v) => this.isVertexOrphaned(v))) {
      return true;
    }
    let joinedZeros = 0;
    for (let i = 0; i < activeBranches.length; i++) {
      const joined = _.intersection(uniqueConnections, activeUserBranchingVerticies);
      if (joined.length === activeUserBranchingVerticies.length) {
        return true;
      }
      if (joined.length === 0) {
        joinedZeros++;
      }
    }
    if (joinedZeros === activeBranches.length) {
      return true;
    }
    return false;
  }

  _isReachableFromVertex(source, target, searchDown, walkState = null) {
    const dir = searchDown ? 'down' : 'up';
    let edges = [];
    if (!walkState) {
      edges = this._getAllEdgesForVertex(this._graph[source]);
    } else {
      const filterDir = searchDown ? 'up' : 'down';
      edges = this._getAllEdgesForVertex(this._graph[walkState.currentVertex]).filter((edge) => edge[filterDir] === walkState.lastVertex);
    }
    edges = edges.map((edge) => edge[dir]).filter((edge) => edge !== null);
    if (edges.includes(target)) {
      return true;
    }
    for (let i = 0; i < edges.length; i++) {
      if (this._isReachableFromVertex(source, target, searchDown, { lastVertex: walkState ? walkState.currentVertex : source, currentVertex: edges[i] })) {
        return true;
      }
    }
    return false;
  }

  /**
   * tensorThree: a 3 level deep tensor (meaning an array of arrays three levels down. At the base of this tensor are a list of field terminals/verticies.
   * activeUserBranchingVerticies: Array of verticies for user branches.
   *
   */
  ensureAtLeastOneReachable(tensorThree, activeUserBranchingVerticies = []) {
    function reduceTensorDimensionality(tensor) {
      // Pare down the state to a rank 2 tensor/array. Returning true if any of the fields at the 3rd level were true.
      // Each entry in rank two now represents a group of consent field types which all must present to be considered a valid set. (i.e. GDPR and US vs SMS_opt_in )
      // intermediateState will now indicate if a given consent group (both traditionals or backup) valid.
      tensor.forEach((outer, idx) => outer.forEach((inner, innerIdx) => {
        tensor[idx][innerIdx] = _.some(inner);
      }));

      // Pare down the state to a rank 1 tensor/array by ensuring that the entirely of a consent group is valid or setting false if invalid.
      // intermediateState will now indicate if any set of consent groups are valid.
      for (let idx = 0; idx < tensor.length; idx++) {
        const stateSlice = tensor[idx];
        if (stateSlice.every((bool) => bool)) {
          tensor[idx] = true;
        } else if (stateSlice.every((bool) => !bool)) {
          tensor[idx] = false;
        } else {
          return false;
        }
      }
      // Now a rank 1 tensor, check that there are no consent groups which are not valid.
      return _.some(tensor);
    }

    // Convert the tensor param into a equally nested array of arrays where the base values are booleans indicating if you can get to a given field from root.
    const rootValidState = tensorThree.map((tensorTwo) => tensorTwo.map((entry) => entry ?
      entry.map((vertex) => _.intersection(this.getAllVerticiesInPathsToRoot(vertex), activeUserBranchingVerticies).length === 0) : [false]
    ));

    if (!activeUserBranchingVerticies.length) {
      return reduceTensorDimensionality(rootValidState);
    }

    for (let i = 0; i < activeUserBranchingVerticies.length; i++) {
      const intermediateState = _.cloneDeep(rootValidState);
      const source = activeUserBranchingVerticies[i];

      tensorThree.forEach((tensorTwo, tIndex) => tensorTwo.forEach((entry, vIndex) => {
        if (entry) {
          entry.forEach((vertex, innerVIndex) => {
            // For each entry at the base (now level 3) of the tensor state, check if you get to that field from this user branch in the loop.
            // Each entry at rank three now represents a single type of consent.
            // intermediateState will now indicate true for all consent type fields which can be reached either from root (all branches) or this specific branch.
            if (!intermediateState[tIndex][vIndex][innerVIndex]) {
              intermediateState[tIndex][vIndex][innerVIndex] = vertex ? this._isReachableFromVertex(source, vertex, true, null) : false;
            }
          });
        }
      }));
      if (!reduceTensorDimensionality(intermediateState)) {
        return false;
      }
    }
    return true;
  }

  areGroupMembersUniquelyReachable(vertexGroup, rootEquivVerticies = [0]) {
    // we're making some assumptions here because we want to avoid walking the whole graph and comparing it to every other field
    // we assume that branching verticies are the first ancestor of a field and being uniquely connected to those
    // and not connected to 0 means you're uniquely reachable
    const group = vertexGroup.map((v) => this._findVertexIndex(v));
    const activeBranches = Object.keys(this._userBranches);
    const reachability = { verticiesUniquelyReachable: true, multiplyReachable: {} };
    if (activeBranches.length === 0 && group.length > 1) {
      reachability.verticiesUniquelyReachable = false;
      reachability.multiplyReachable[0] = [...group];
      return reachability;
    }
    if (activeBranches.length === 0) {
      return reachability;
    }
    if (group.length === 1) {
      return reachability;
    }
    const upEdgeMap = {};
    group.forEach((v) => this._getAllEdgesForVertex(this._graph[v]).forEach((e) => { if (upEdgeMap[e.up]) { upEdgeMap[e.up].push(v); } else { upEdgeMap[e.up] = [v]; } }));
    const edgeList = Object.keys(upEdgeMap);
    rootEquivVerticies.forEach((root) => {
      if (edgeList.length > 1 && upEdgeMap[root]) {
        reachability.verticiesUniquelyReachable = false;
        reachability.multiplyReachable[0] = reachability.multiplyReachable[0] ? [...reachability.multiplyReachable[0], ...upEdgeMap[root]] : [...upEdgeMap[root]];
      }
    });
    edgeList.forEach((edge) => {
      if (upEdgeMap[edge].length > 1 || reachability.multiplyReachable[0]) {
        reachability.verticiesUniquelyReachable = false;
        reachability.multiplyReachable[edge] = [...upEdgeMap[edge]];
      }
    });
    return reachability;
  }

  getVertexDetails(vertex) {
    if (typeof vertex !== 'number') return vertex;
    return { left: this._graph[vertex].left, op: this._graph[vertex].op, right: this._graph[vertex].right };
  }

  exportConditionStrings(activeBranchAnswers, replacements = {}) {
    return this.getRoots().reduce((conds, rootIndex) => {
      if (rootIndex && !this.isVertexOrphaned(rootIndex)) {
        conds[this._graph[rootIndex].left] = this._makeConditionStringForRoot(rootIndex, activeBranchAnswers, replacements).trim();
      }
      return conds;
    }, {});
  }

  importConditionString(fieldVertex, condition, stateEntryToFieldIdMap = {}) {
    const vertexPass = parseCondition(condition).reduce((memo, nextToken) => {
      memo.stack.push(nextToken);
      return { stack: tryReduceVertex(memo.stack, this, stateEntryToFieldIdMap) };
    }, { stack: [] });
    const conditionTree = { vertex: 0, up: null, down: [] };
    makeTree(conditionTree, vertexPass.stack);

    insertIntoGraph(this, fieldVertex, conditionTree);
  }
}
